import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Save,
  FilePlus,
  FolderOpen,
  Trash2,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Circle,
} from "lucide-react";

function computePos(trigger) {
  if (!trigger) return null;
  const r = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 8;

  if (vw < 640) {
    return {
      top: Math.round(Math.max(r.bottom + 4, 56)),
      left: margin,
      width: vw - margin * 2,
      maxHeight: Math.round(vh - (r.bottom + 4) - 72),
    };
  }
  const width = Math.min(240, vw - margin * 2);
  let left = r.left;
  if (left + width + margin > vw) left = vw - width - margin;
  if (left < margin) left = margin;
  return {
    top: Math.round(r.bottom + 4),
    left: Math.round(left),
    width,
    maxHeight: Math.round(vh - r.bottom - 24),
  };
}

function SaveBadge({ saveState, isDirty, isProjectMode }) {
  if (!isProjectMode) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-ide-mute font-mono">
        <Circle className="h-3 w-3" /> scratchpad
      </span>
    );
  }
  if (saveState === "saving") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-sky-300 font-mono">
        <Loader2 className="h-3 w-3 animate-spin" /> saving
      </span>
    );
  }
  if (saveState === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-rose-300 font-mono">
        <AlertTriangle className="h-3 w-3" /> save failed
      </span>
    );
  }
  if (isDirty) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-amber-300 font-mono">
        <Circle className="h-3 w-3" /> unsaved
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 font-mono">
      <CheckCircle2 className="h-3 w-3" /> saved
    </span>
  );
}

export default function ProjectMenu({
  projectName,
  isProjectMode,
  saveState,
  isDirty,
  onNew,
  onOpen,
  onSave,
  onSaveAs,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open) return;
    const recompute = () => setPos(computePos(triggerRef.current));
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function close() { setOpen(false); }
  function toggle() {
    // eslint-disable-next-line no-console
    console.log("[noefforts] project menu clicked, was open=", open);
    setOpen((o) => !o);
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-ide-border bg-ide-panel hover:bg-ide-surface hover:border-ide-borderHi transition-colors text-sm min-w-[180px] max-w-[260px] min-h-[36px]"
        onClick={toggle}
      >
        <span className="truncate text-ide-text font-medium">{projectName || "Scratchpad"}</span>
        <SaveBadge saveState={saveState} isDirty={isDirty} isProjectMode={isProjectMode} />
        <ChevronDown className="ml-auto h-4 w-4 text-ide-mute" />
      </button>

      {createPortal(
        <AnimatePresence>
          {open && pos && (
            <>
              <motion.div
                key="pm-bg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
                onClick={close}
                style={{ zIndex: 98 }}
                className="fixed inset-0 bg-transparent"
              />
              <motion.div
                key="pm-fg"
                initial={{ opacity: 0, y: -4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ duration: 0.14, ease: "easeOut" }}
                style={{
                  position: "fixed",
                  top: pos.top,
                  left: pos.left,
                  width: pos.width,
                  maxHeight: pos.maxHeight,
                  zIndex: 100,
                  pointerEvents: "auto",
                }}
                className="rounded-lg border border-ide-border bg-ide-panel shadow-soft py-1 overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <Item icon={<FilePlus className="h-3.5 w-3.5" />} onClick={() => { onNew?.(); close(); }}>
                  New project
                </Item>
                <Item icon={<FolderOpen className="h-3.5 w-3.5" />} onClick={() => { onOpen?.(); close(); }}>
                  Open…
                </Item>
                <Sep />
                <Item icon={<Save className="h-3.5 w-3.5" />} onClick={() => { onSave?.(); close(); }}
                  kbd={"\u2318/Ctrl+S"}>
                  Save
                </Item>
                <Item icon={<Save className="h-3.5 w-3.5" />} onClick={() => { onSaveAs?.(); close(); }}>
                  Save As…
                </Item>
                <Sep />
                <Item icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => { onDelete?.(); close(); }}
                  disabled={!isProjectMode} danger>
                  Delete…
                </Item>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

function Item({ icon, children, onClick, disabled, danger, kbd }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        "flex items-center gap-2 w-full px-3 py-2 text-left text-sm transition-colors min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed " +
        (danger ? "text-rose-300 hover:bg-rose-500/10" : "text-ide-text hover:bg-ide-surface")
      }
    >
      {icon}
      <span className="flex-1">{children}</span>
      {kbd && (
        <span className="text-[10px] text-ide-mute font-mono px-1 py-0.5 rounded border border-ide-border">
          {kbd}
        </span>
      )}
    </button>
  );
}

function Sep() {
  return <div className="h-px bg-ide-border my-1" />;
}
